'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// 소수점 스크롤 위치 때문에 끝에 닿아도 1px 이 남는 경우가 있다.
const SCROLL_EPSILON = 2;

/**
 * 좌우로 넘기는 카드 줄 - 「신청 가능한 프로그램」과 학생 홈 「내 참여 프로그램」이 같이 쓴다(데모 피드백 F11).
 * 화살표는 갈 수 없는 방향이어도 자리를 지키고 잠긴다(AGENTS.md: 사라졌다 나타나면 카드 줄 폭이 흔들린다).
 * 좁은 화면(sm 아래)에서는 화살표 없이 손가락으로 넘긴다(scroll-snap).
 */
export function HorizontalScroller({ children, itemCount, previousLabel, nextLabel, testId, trackClassName }: {
  children: ReactNode;
  /** 카드 수가 바뀌면 화살표를 다시 잰다. */
  itemCount: number;
  previousLabel: string;
  nextLabel: string;
  testId?: string;
  trackClassName?: string;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setCanScrollLeft(track.scrollLeft > SCROLL_EPSILON);
    setCanScrollRight(track.scrollLeft < max - SCROLL_EPSILON);
  }, []);

  // 카드가 그려진 뒤, 그리고 창 크기가 바뀔 때마다 화살표 활성 여부를 다시 잰다.
  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState, itemCount]);

  const scrollBy = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    // 보이는 폭의 대부분을 한 번에 넘긴다: 카드 한 장씩 끊어 이동하는 것보다
    // 손이 덜 가고, 카드 폭이 달라져도 동작이 같다.
    track.scrollTo({
      left: track.scrollLeft + direction * Math.max(track.clientWidth * 0.8, 240),
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className="flex items-stretch gap-2">
      <ScrollArrow label={previousLabel} disabled={!canScrollLeft} onClick={() => scrollBy(-1)}>
        <ChevronLeft className="h-4 w-4" />
      </ScrollArrow>
      <div ref={trackRef} data-testid={testId} onScroll={updateScrollState}
        className={cn('flex min-w-0 flex-1 snap-x gap-4 overflow-x-auto pb-2', trackClassName)}>
        {children}
      </div>
      <ScrollArrow label={nextLabel} disabled={!canScrollRight} onClick={() => scrollBy(1)}>
        <ChevronRight className="h-4 w-4" />
      </ScrollArrow>
    </div>
  );
}

function ScrollArrow({ label, disabled, onClick, children }: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button type="button" variant="outline" size="icon" className="my-auto hidden h-9 w-9 shrink-0 sm:inline-flex"
      aria-label={label} disabled={disabled} onClick={onClick}>
      {children}
    </Button>
  );
}
